import React, { useState } from 'react';
import { NEUTRAL } from './designTokens';
import { BookingDetailPanel } from './BookingDayBand';

// T-12: Kommande som agenda. Datum i vänsterkolumn, bokningarna (tid · namn · tjänst)
// under varandra per dag. Klick på rad fäller ut detaljpanelen (anteckning, kundkort,
// avboka). Klick på datumet visar den dagen i den mörka dagboxen ovanför.

const DAY_NAMES_SHORT = ['Sön', 'Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör'];
const MONTHS_SHORT = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
const hm = (t) => (t || '').slice(0, 5);

function BookingUpcomingView({
  theme,
  bookings,
  newBookingIds = new Set(),
  getDateStr,
  cancelBooking,
  saveNote,
  onOpenClient,
  onPickDate,
}) {
  const [openId, setOpenId] = useState(null);
  const today = getDateStr(new Date());
  const upcoming = bookings.filter(b => b.date > today);
  const thisMonth = new Date().getMonth();

  const grouped = upcoming.reduce((acc, b) => {
    if (!acc[b.date]) acc[b.date] = [];
    acc[b.date].push(b);
    return acc;
  }, {});
  const dates = Object.keys(grouped).sort();

  return (
    <div style={{ background: theme.card_bg, border: `1px solid ${theme.card_border}`, borderRadius: theme.card_radius, boxShadow: theme.card_shadow, overflow: 'hidden' }}>
      <div style={{ padding: '16px 24px', borderBottom: `1px solid ${theme.card_border}`, display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: NEUTRAL.ink, fontFamily: 'var(--font-heading)' }}>Kommande</h2>
        <span style={{ fontSize: '13px', color: NEUTRAL.muted }}>{upcoming.length} {upcoming.length === 1 ? 'bokning' : 'bokningar'} efter idag</span>
      </div>

      {dates.length === 0 ? (
        <div style={{ margin: '16px 24px', padding: '28px', textAlign: 'center', borderRadius: '10px', border: `1px dashed ${theme.card_border}` }}>
          <p style={{ margin: '0 0 4px', fontWeight: '600', color: NEUTRAL.text }}>Inga kommande bokningar</p>
          <p style={{ margin: 0, color: NEUTRAL.muted, fontSize: '13px' }}>Boka en tid i dagboxen ovanför eller i kalendern</p>
        </div>
      ) : dates.map((date, di) => {
        const d = new Date(date + 'T12:00:00');
        const showMonth = d.getMonth() !== thisMonth;
        return (
          <div key={date} className="fr-upcoming-day" style={{ display: 'flex', gap: '24px', padding: '14px 24px', borderBottom: di < dates.length - 1 ? `1px solid ${theme.card_border}` : 'none' }}>
            <button
              type="button"
              onClick={() => onPickDate && onPickDate(d)}
              title="Visa dagen"
              style={{ width: '56px', flexShrink: 0, textAlign: 'center', paddingTop: '4px', background: 'none', border: 'none', cursor: onPickDate ? 'pointer' : 'default', fontFamily: 'inherit', alignSelf: 'flex-start' }}
            >
              <div style={{ fontSize: '11px', fontWeight: '600', letterSpacing: '0.08em', textTransform: 'uppercase', color: NEUTRAL.muted }}>{DAY_NAMES_SHORT[d.getDay()]}</div>
              <div style={{ fontFamily: 'var(--font-heading)', fontSize: '24px', fontWeight: '700', color: NEUTRAL.ink, lineHeight: 1.1 }}>{d.getDate()}</div>
              {showMonth && <div style={{ fontSize: '10px', color: NEUTRAL.muted }}>{MONTHS_SHORT[d.getMonth()]}</div>}
            </button>
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
              {grouped[date].map(b => {
                const isOpen = openId === b.id;
                const isNew = newBookingIds.has(b.id);
                const service = b.service_title_snapshot || b.service?.title || null;
                const dur = (() => { const [sh, sm] = hm(b.start_time).split(':').map(Number); const [eh, em] = hm(b.end_time).split(':').map(Number); return `${(eh * 60 + em) - (sh * 60 + sm)} min`; })();
                return (
                  <div key={b.id}>
                    <button
                      type="button"
                      onClick={() => setOpenId(isOpen ? null : b.id)}
                      className="fr-upcoming-row"
                      style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 12px', borderRadius: '8px', background: isOpen ? NEUTRAL.page : 'transparent', border: 'none', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left', boxSizing: 'border-box' }}
                    >
                      <div style={{ width: '92px', flexShrink: 0, fontSize: '13px', color: NEUTRAL.text, fontVariantNumeric: 'tabular-nums' }}>{hm(b.start_time)}–{hm(b.end_time)}</div>
                      <div style={{ flex: 1, minWidth: 0, fontSize: '14px', color: NEUTRAL.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <span style={{ fontWeight: '600' }}>{b.client?.name || '–'}</span>
                        {service && <span style={{ color: NEUTRAL.muted }}> · {service} {dur}</span>}
                      </div>
                      {isNew
                        ? <span style={{ borderRadius: '999px', padding: '2px 8px', background: theme.accent_soft_bg, color: theme.accent_soft_text, fontSize: '11px', fontWeight: '600' }}>Ny</span>
                        : <span aria-hidden="true" style={{ fontSize: '12px', color: NEUTRAL.line }}>{isOpen ? '▴' : '▾'}</span>}
                    </button>
                    {isOpen && (
                      <div style={{ padding: '4px 0 8px' }}>
                        <BookingDetailPanel
                          theme={theme}
                          booking={b}
                          onOpenClient={onOpenClient}
                          onSaveNote={saveNote}
                          onCancel={async (id) => { await cancelBooking(id); setOpenId(null); }}
                          onClose={() => setOpenId(null)}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default BookingUpcomingView;
